import { PageContent, PageType } from "@boark/shared/types/page";
import { Editor } from "@tinymce/tinymce-react";
import { useRef, useState } from "react";
import { Editor as TinyMCEEditor } from "tinymce";

import {
  uploadDataUriToMinio,
  uploadFileToMinio,
} from "../services/file-upload";
import { updatePage } from "../services/page";
import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Input } from "./input";
import { Select } from "./select";
import { TinyMCE } from "./tinymce";

export function PageEditor({
  page,
  onCancel,
  onSave,
  submitButtonText,
  cancelButtonText,
}: {
  page: PageContent;
  onCancel?: () => void;
  onSave?: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
}) {
  const { currentLanguage, locale: t } = useLocale();

  const [title, setTitle] = useState(page.title || "");
  const [type, setType] = useState<PageType>(page.type);
  const editorRef = useRef<Editor>(null);

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        (async () => {
          const editor = editorRef.current?.editor;
          if (type === "content" && !editor) {
            return;
          }
          if (type === "content" && editor) {
            editor.readonly = true;
            await uploadImagesInTinyMCEEditor(editor);
          }

          await updatePage(page.id, currentLanguage, {
            title,
            type,
            ...(type === "content" && editor
              ? { content: editor.getContent() }
              : {}),
          });

          if (type === "content" && editor) {
            editor.readonly = false;
          }

          onSave?.();
        })();
      }}
    >
      <Input
        placeholder="Title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Select
        label={t.page.type}
        options={[
          { label: t.page.typeContent, value: "content" },
          { label: t.page.typeRoles, value: "roles" },
        ]}
        value={type}
        onChange={(value) => setType(value as PageType)}
      />
      {type === "content" && (
        <TinyMCE
          ref={editorRef}
          value={page.content}
          onFilePickerCallback={(file: File) => {
            return uploadFileToMinio(file, true);
          }}
        />
      )}
      <div className="flex justify-end gap-2">
        <Button severity="secondary" onClick={onCancel}>
          {cancelButtonText || "Cancel"}
        </Button>
        <Button type="submit">{submitButtonText || "Save"}</Button>
      </div>
    </form>
  );
}

async function uploadImagesInTinyMCEEditor(
  editor: TinyMCEEditor,
): Promise<void> {
  const tinyMCEDoc = editor.getDoc();
  const blobImages = Array.from(tinyMCEDoc.querySelectorAll("img")).filter(
    (img) => img.src.startsWith("blob:"),
  );

  for (const blobImage of blobImages) {
    const src = blobImage.getAttribute("src");
    if (src === null) {
      continue;
    }
    const url = await uploadDataUriToMinio(src, true);
    blobImage.setAttribute("src", url);
  }
}
